import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, Shield, Wrench, Clock, DollarSign } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function MooseJawReplacement() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does roof replacement cost in Moose Jaw?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "In Moose Jaw, a standard asphalt shingle roof replacement typically ranges from $6,000 to $14,000. Factors include the size of your roof, pitch, accessibility, and the specific materials chosen. We always provide a fixed-price written quote.",
        },
      },
      {
        "@type": "Question",
        name: "How long does a roof replacement take?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most residential roof replacements in Moose Jaw are completed within 1 to 2 days. We prioritize minimizing disruption to your daily life while ensuring high-quality workmanship.",
        },
      },
      {
        "@type": "Question",
        name: "Do I need to replace my roof or just repair it?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "If your roof is over 15-20 years old, has extensive granule loss, widespread curling shingles, or multiple active leaks, replacement is usually the most cost-effective long-term solution. A free inspection can determine the exact condition.",
        },
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": "https://www.saskroofingcompany.com/#localbusiness",
    name: "Sask Roofing Company",
    url: "https://www.saskroofingcompany.com/",
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: "Moose Jaw",
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roof Replacement",
    provider: {
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Moose Jaw",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.saskroofingcompany.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: "https://www.saskroofingcompany.com/areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Moose Jaw",
        item: "https://www.saskroofingcompany.com/areas/moose-jaw",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Replacement",
        item: "https://www.saskroofingcompany.com/areas/moose-jaw/replacement",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Roof Replacement Moose Jaw | Sask Roofing Company"
        description="Expert roof replacement in Moose Jaw, SK. We install premium roofs built to withstand Saskatchewan's extreme weather. Get an honest, fixed-price quote today."
        schema={[
          faqSchema,
          localBusinessSchema,
          serviceSchema,
          breadcrumbSchema,
        ]}
      />
      <div className="flex flex-col min-h-screen">
        <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/01402efe-8f21-42b2-bc66-0286199a2300.png&output=webp&q=60"
              alt="Moose Jaw Roof Replacement"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Roof Replacement Moose Jaw
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                When your Moose Jaw home needs a new roof, you need a contractor
                who understands the local climate. We install premium roofing
                systems designed to withstand high prairie winds, heavy snow
                loads, and extreme temperature shifts. Honest pricing, local
                crews, and zero surprises.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">Get Your Free Inspection</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  When Is It Time for a Roof Replacement?
                </h2>
                <p className="text-lg text-muted-foreground">
                  Moose Jaw's weather is notoriously tough on exteriors. Between
                  the blistering summer sun, sudden hailstorms, and deep winter
                  freezes, even the best shingles eventually wear down. Delaying
                  a replacement can lead to hidden water damage, compromised
                  insulation, and structural rot.
                </p>
                <div className="space-y-4 pt-4">
                  <h3 className="text-xl font-semibold">
                    Watch for these common signs:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Clock className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Age:</strong> Your current asphalt roof is
                        approaching or has surpassed 20 years of age.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Shield className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Visible Wear:</strong> Shingles are curling,
                        cracking, or missing entirely after a strong windstorm.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Wrench className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Granule Loss:</strong> You're finding excessive
                        shingle granules in your eavestroughs, leaving the
                        asphalt exposed to UV damage.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <DollarSign className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Energy Spikes:</strong> Unexplained increases in
                        heating or cooling costs, often pointing to compromised
                        roof ventilation and insulation.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/3b1c76bb-88af-4db0-9bd6-efd0d1599e86.png&output=webp&q=60"
                  alt="Roof installation process in Moose Jaw"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
              <h2 className="text-3xl font-bold">
                Built for Moose Jaw Roof Replacement
              </h2>
              <p className="text-lg text-muted-foreground">
                We don't just nail down shingles; we build complete roofing
                systems engineered for the prairies. A standard builder-grade
                roof won't cut it when the temperatures drop to -40°C.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">
                  Ice Damming Prevention
                </h3>
                <p className="text-muted-foreground">
                  We install premium ice and water shields along all eaves and
                  valleys to protect against the freeze-thaw cycles that plague
                  Moose Jaw winters, preventing water from backing up under your
                  shingles.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">High-Wind Resistance</h3>
                <p className="text-muted-foreground">
                  Our architectural shingles are rated for high wind speeds.
                  Combined with proper nailing techniques (we never cut corners
                  on fasteners), your roof stays intact during severe prairie
                  storms.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Advanced Ventilation</h3>
                <p className="text-muted-foreground">
                  Proper airflow is critical. We ensure your attic breathes
                  correctly, which extends the life of your roof, reduces summer
                  cooling costs, and prevents winter condensation issues.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Building Permits and Code in Moose Jaw
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Navigating local building codes can be confusing, but we
                  handle it for you. In Moose Jaw, a standard residential roof
                  replacement (where we are simply tearing off old shingles and
                  installing new ones over existing decking) typically does not
                  require a building permit. However, if your roof has suffered
                  structural damage—such as broken trusses from heavy snow loads
                  or rotted decking that needs extensive replacement—a permit
                  from the City of Moose Jaw may be required. We assess your
                  roof's structural integrity during our free inspection and
                  manage all necessary permitting and compliance, ensuring your
                  new roof meets all local and provincial building codes.
                </p>
              </div>
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Insurance and Storm Claims in Moose Jaw
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Moose Jaw is no stranger to severe summer hailstorms and high
                  wind events that can devastate a roof in minutes. If your roof
                  has been damaged by a storm, dealing with insurance can be
                  stressful. We specialize in storm damage restoration and
                  frequently work with local insurance adjusters. If you suspect
                  damage, call us for a free inspection before filing a claim.
                  We will document the damage with detailed photos and provide a
                  comprehensive report for your adjuster, ensuring you get the
                  full coverage you are entitled to for a proper roof
                  replacement.
                </p>
              </div>
            </div>

            <div className="mb-20">
              <div className="bg-card p-8 rounded-xl border shadow-sm max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4 text-center">
                  Materials and Warranties for Moose Jaw Roofs
                </h3>
                <p className="text-muted-foreground leading-relaxed text-center mb-6">
                  For Moose Jaw's demanding climate, we typically recommend
                  premium architectural shingles from trusted manufacturers like
                  IKO, CertainTeed, or GAF. Specifically, we often suggest Class
                  4 impact-resistant shingles to combat the frequent hailstorms
                  in the region. These shingles are engineered to withstand
                  severe weather and come with robust manufacturer warranties.
                  When combined with our local workmanship warranty, you get a
                  roofing system that provides long-lasting peace of mind
                  against the harsh Saskatchewan elements.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      250+
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Roofs Completed in Moose Jaw
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Class 4
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Impact Resistance
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      130km/h
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Wind Rating
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100%
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Local Crews
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/3b1c76bb-88af-4db0-9bd6-efd0d1599e86.png&output=webp&q=60"
                  alt="Moose Jaw roofing crew"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 className="text-3xl font-bold">
                  Our Moose Jaw Roof Replacement Process
                </h2>
                <p className="text-lg text-muted-foreground">
                  We believe in transparency and doing the job right the first
                  time. Here is what you can expect when you hire Sask Roofing
                  Company.
                </p>
                <ol className="space-y-6 pt-4">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Comprehensive Inspection
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We assess your entire roofing system, including the
                        deck, flashing, and ventilation, to identify all
                        underlying issues.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Fixed-Price Quote
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        You receive a detailed, written estimate. What you see
                        is what you pay—no hidden fees or surprise upcharges
                        halfway through the job.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Tear-Off & Installation
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        Our local Moose Jaw crew removes your old roof down to
                        the deck, inspects the wood, and installs your new
                        premium roofing system.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Immaculate Clean-Up
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We protect your landscaping during the build and perform
                        a thorough magnetic sweep of your property to ensure no
                        stray nails are left behind.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-muted/10 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label="Moose Jaw roofing pages"
              className="bg-background p-6 rounded-xl border shadow-sm"
            >
              <p className="font-bold text-lg mb-4 text-foreground">
                More roofing services in Moose Jaw:
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/areas/moose-jaw"
                    className="text-primary hover:underline font-medium"
                  >
                    ← All Moose Jaw roofing services (overview)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas/moose-jaw/repair"
                    className="text-primary hover:underline font-medium"
                  >
                    Roof Repair in Moose Jaw
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    All Sask roofing services
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <section className="py-20 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Moose Jaw Roof Replacement FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-background rounded-xl border p-2 shadow-sm"
            >
              <AccordionItem value="item-1" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How much does roof replacement cost in Moose Jaw?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  In Moose Jaw, a standard asphalt shingle roof replacement
                  typically ranges from $6,000 to $14,000. Factors include the
                  size of your roof, pitch, accessibility, and the specific
                  materials chosen. We always provide a fixed-price written
                  quote so you know the exact cost upfront.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How long does a roof replacement take?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Most residential roof replacements in Moose Jaw are completed
                  within 1 to 2 days. We prioritize minimizing disruption to
                  your daily life while ensuring high-quality workmanship.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Do I need to replace my roof or just repair it?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  If your roof is over 15-20 years old, has extensive granule
                  loss, widespread curling shingles, or multiple active leaks,
                  replacement is usually the most cost-effective long-term
                  solution. A free inspection can determine the exact condition.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Ready to Protect Your Moose Jaw Home?
            </h2>
            <p className="text-xl opacity-90">
              Get a free, no-pressure inspection and a fixed-price quote from
              Saskatchewan's trusted roofing experts.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Get Your Free Roof Inspection</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
